import ActionCard from "./ActionCard/ActionCard";
import { useActionsData } from "../../../store/selectors/actionModulesSelectors";
import { useTableDataToRender } from "../../../hooks/UseTableDataToRender";
import { useEffect, useState, useCallback } from "react";
import { ActionEdit } from "./ActionEdit";
import { Box, Dialog, Stack } from '@mui/material';
import { useAppDispatch } from "../../../hooks/StoreHooks";
import { fetchGroupsThunk } from "../../../store/thunk/groupsThunk";
import { useGroupsData } from "../../../store/selectors/groupSelectors";
import { PermissionEntityType } from "../../../types/permissionType";
import { usePermissionChecker } from "../../../hooks/usePermissionChecker";
import { ActionModule } from "../../../types/actionModule/actionModule";
import { useDrag, useDrop } from 'react-dnd';
import { updateActionModuleOrderThunk } from "../../../store/thunk/actionsModulesThunk";

interface Item {
  id: number;
  originalIndex: number;
}

interface ActionCardTileProps {
  id: number;
  action: ActionModule;
  groupsList: any[];
  onEditClicked: () => void;
  moveTile: (id: number, to: number) => void;
  findTile: (id: number) => { action: ActionModule, index: number };
  isReordering: boolean;
  setIsReordering: (value: boolean) => void;
}

function ActionCardTile(props: ActionCardTileProps) {
  const { index: originalIndex } = props.findTile(props.id);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'ActionModuleTile',
      item: { id: props.id, originalIndex },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: (item: Item, monitor) => {
        const { id: droppedId, originalIndex } = item;
        const didDrop = monitor.didDrop();

        if (!didDrop) {
          props.moveTile(droppedId, originalIndex);
        }
      }
    }),
    [props.id, originalIndex, props.moveTile]
  );

  const [, drop] = useDrop({
    accept: 'ActionModuleTile',
    hover: (item: Item) => {
      if (item.id !== props.id) {
        const { index } = props.findTile(props.id);
        props.moveTile(item.id, index);
      }
    },
  });

  return (
    <Box
      key={props.action.id}
      sx={{ width: "33%" }}
      ref={(node: HTMLDivElement | null) => drag(drop(node))}
      style={{ opacity: isDragging ? 0.25 : 1 }}
    >
      <Box sx={{ p: 2 }}>
        <ActionCard
          actionItem={props.action}
          groupsList={props.groupsList}
          onEditClicked={props.onEditClicked}
        />
      </Box>
    </Box>
  );
}

const ActionCards = (props: { searchQuery: string }) => {
  const dispatch = useAppDispatch();
  const actionsData = useActionsData();
  const groupsList = useGroupsData();

  const [clickedActionCard, setClickedActionCard] = useState<ActionModule | undefined>(undefined);
  const [draggedModules, setDraggedModules] = useState<ActionModule[]>([]);
  const [isReordering, setIsReordering] = useState(false);

  const tableDataToRender = useTableDataToRender(
    props.searchQuery,
    actionsData,
    "name"
  );

  const { userHasPermission } = usePermissionChecker();
  const canEditActionModule = (action: ActionModule) => {
    return userHasPermission(PermissionEntityType.ActionModules, "Edit") ||
      userHasPermission(PermissionEntityType.ActionModules, "Edit", action.id);
  };

  useEffect(() => {
    if (!groupsList.length) {
      dispatch(fetchGroupsThunk());
    }
  }, [dispatch, groupsList.length]);

  useEffect(() => {
    if (tableDataToRender) {
      setDraggedModules([...tableDataToRender].sort((a, b) => a.order - b.order));
    }
  }, [tableDataToRender]);

  const [, drop] = useDrop(() => ({
    accept: 'ActionModuleTile',
    drop: () => dispatch(updateActionModuleOrderThunk(draggedModules.map(m => m.id)))
  }), [draggedModules]);

  const findTile = useCallback((id: number) => {
    const action = draggedModules.filter((m) => m.id === id)[0];
    return {
      action,
      index: draggedModules.indexOf(action)
    };
  }, [draggedModules]);

  const moveTile = useCallback((id: number, to: number) => {
    const { action, index } = findTile(id);
    const updatedTiles = [...draggedModules];
    updatedTiles.splice(index, 1);
    updatedTiles.splice(to, 0, action);
    const reorderedTiles = updatedTiles.map((m, i) => ({ ...m, order: i }));
    setDraggedModules(reorderedTiles);
  }, [draggedModules, findTile]);

  const handleEditClicked = (action: ActionModule) => {
    if (canEditActionModule(action)) {
      setClickedActionCard(action);
    }
  };

  return (
    <>
      <Stack direction="row" sx={{ flexWrap: "wrap" }} ref={drop}>
        {draggedModules.map((action: ActionModule) => (
          <ActionCardTile
            key={action.id}
            id={action.id}
            action={action}
            groupsList={groupsList}
            onEditClicked={() => handleEditClicked(action)}
            moveTile={moveTile}
            findTile={findTile}
            isReordering={isReordering}
            setIsReordering={setIsReordering}
          />
        ))}
      </Stack>

      <Dialog
        open={!!clickedActionCard}
        onClose={() => setClickedActionCard(undefined)}
        fullWidth
        maxWidth="lg">
        {clickedActionCard && <ActionEdit
          actionItem={clickedActionCard}
          onSaved={() => setClickedActionCard(undefined)} />}
      </Dialog>
    </>
  );
};

export default ActionCards;
